import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import { useLanguage } from '../../contexts/LanguageContext';

// Lets an admin change what the Pro plan costs, without touching code.
// Only Pro has a price to edit — Free is always $0 and Enterprise is
// contact-sales. Reads/writes GET|PUT /admin/plans/pricing, which stores
// the override in the same platform_settings collection used by the
// registration toggle and "coming soon" sections. The public pricing
// shown on the Homepage and the in-app upgrade screen both read this same
// value live (no cache), and it's also what a NEW MercadoPago subscription
// checkout charges — existing already-authorized subscriptions keep
// billing at whatever price they were created at.
const AdminPlansTab = () => {
  const { t, lang } = useLanguage();
  const priceLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const [priceClp, setPriceClp] = useState('');
  const [priceClpAnnual, setPriceClpAnnual] = useState('');
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const loadPricing = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/admin/plans/pricing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPriceClp(String(data.price_clp));
      setPriceClpAnnual(String(data.price_clp_annual));
      setDefaults({ price_clp: data.default_price_clp, price_clp_annual: data.default_price_clp_annual });
    } catch (error) {
      console.error('Error loading plan pricing:', error);
      setMessage({ type: 'error', text: t('adminPlans.loadError', 'No se pudo cargar el precio actual.') });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPricing();
  }, []);

  const handleSave = async () => {
    const monthly = parseInt(priceClp, 10);
    const annual = parseInt(priceClpAnnual, 10);
    if (!monthly || monthly <= 0 || !annual || annual <= 0) {
      setMessage({ type: 'error', text: t('adminPlans.invalidAmounts', 'Ingresa montos válidos, mayores a 0.') });
      return;
    }
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/plans/pricing`,
        { price_clp: monthly, price_clp_annual: annual },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: t('adminPlans.saved', 'Precio actualizado. Ya se refleja en el Homepage y en la pantalla de suscripción.') });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error saving plan pricing:', error);
      setMessage({ type: 'error', text: error?.response?.data?.detail || t('adminPlans.saveError', 'No se pudo guardar el precio.') });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6">{t('adminPlans.loading', 'Cargando precios...')}</div>;
  }

  return (
    <div className="bg-white card-shadow-lg rounded-xl p-6" style={{ maxWidth: 480 }}>
      <h3 className="text-base font-semibold text-black">{t('adminPlans.title', 'Precio del plan Pro')}</h3>
      <p className="text-gray-500 text-sm mt-1 mb-5">
        {t('adminPlans.subtitle', 'Free siempre es gratis y Enterprise se cotiza directamente con ventas — solo Pro tiene un precio editable aquí.')}
      </p>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminPlans.monthlyPrice', 'Precio mensual (CLP)')}</label>
          <input
            type="number"
            min="1"
            value={priceClp}
            onChange={(e) => setPriceClp(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-sky-400 text-gray-900"
          />
          {defaults && <p className="text-xs text-gray-400 mt-1">{t('adminPlans.originalValue', 'Valor original:')} ${defaults.price_clp.toLocaleString(priceLocale)} CLP</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminPlans.annualPrice', 'Precio anual (CLP)')}</label>
          <input
            type="number"
            min="1"
            value={priceClpAnnual}
            onChange={(e) => setPriceClpAnnual(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-sky-400 text-gray-900"
          />
          {defaults && <p className="text-xs text-gray-400 mt-1">{t('adminPlans.originalValue', 'Valor original:')} ${defaults.price_clp_annual.toLocaleString(priceLocale)} CLP</p>}
        </div>
      </div>

      {message.text && (
        <p className={`text-sm mt-4 ${message.type === 'error' ? 'text-red-600' : 'text-green-700'}`}>
          {message.text}
        </p>
      )}

      <button
        onClick={handleSave}
        disabled={saving}
        className="mt-5 px-5 py-2.5 rounded-lg text-white font-medium text-sm disabled:opacity-50"
        style={{ backgroundColor: '#004AAD' }}
      >
        {saving ? t('adminPlans.saving', 'Guardando…') : t('adminPlans.savePrice', 'Guardar precio')}
      </button>

      <p className="text-xs text-gray-400 mt-4 leading-relaxed">
        {t('adminPlans.footnote', 'Las suscripciones ya activas en MercadoPago siguen cobrando el monto con el que se crearon; este precio aplica a las nuevas suscripciones desde que lo guardas.')}
      </p>
    </div>
  );
};

export default AdminPlansTab;
